<template>
  <v-dialog
    v-if="value"
    :value="value"
    @input="$emit('input', $event)"
    :max-width="maxWidth"
    persistent
    overlay-color="#0f172a"
    overlay-opacity="0.6"
    content-class="ism-pmf-dialog"
    @click:outside="!persistent && $emit('input', false)"
  >
    <div class="ism-pmf-wrap">
      <!-- ── Header ── -->
      <div class="ism-pmf-header">
        <h3 class="ism-pmf-title">{{ title }}</h3>
        <button
          class="ism-pmf-close"
          @click="$emit('input', false)"
          type="button"
        >
          <v-icon size="18" color="var(--accent)">mdi-close</v-icon>
        </button>
      </div>

      <!-- ── Body ── -->
      <div class="ism-pmf-body">
        <slot></slot>
      </div>

      <!-- ── Footer ── -->
      <div class="ism-pmf-footer" v-if="showFooter">
        <button
          class="ism-btn-cancel"
          type="button"
          :disabled="loading"
          @click="$emit('input', false)"
        >
          {{ cancelText }}
        </button>
        <button
          class="ism-pmf-btn-submit"
          type="button"
          :disabled="loading"
          @click="$emit('submit')"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="14"
            width="2"
            color="#fff"
            style="margin-right: 8px"
          />
          {{ submitText }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "PremiumModalForm",

  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    maxWidth: { type: [Number, String], default: 500 },
    submitText: { type: String, default: "Save Changes" },
    cancelText: { type: String, default: "Cancel" },
    loading: { type: Boolean, default: false },
    showFooter: { type: Boolean, default: true },
    persistent: { type: Boolean, default: true },
  },
};
</script>

<style>
/* Non-scoped — targets v-dialog content-class */
/* Non-scoped — targets Vuetify's internal dialog structures */
.v-dialog__content {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

.v-dialog__content::-webkit-scrollbar {
  display: none !important;
}

/* Center popups in the true middle of the page (viewport) */
.v-dialog__content {
  padding-left: 0 !important;
  padding-top: 0 !important;
}

@media (max-width: 600px) {
  .v-dialog__content {
    align-items: flex-end !important; /* Bottom sheet feel on mobile */
    padding-bottom: 20px !important;
  }
}

.ism-pmf-dialog {
  border-radius: 18px !important;
  overflow: visible !important;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22) !important;
  margin: 24px !important; /* Prevents touching screen edges */
  align-self: center !important;
  display: flex !important;
  flex-direction: column !important;
}

.v-dialog:not(.v-dialog--fullscreen) {
  margin: auto !important;
}
</style>

<style scoped>
/* ── Wrapper ─────────────────────────────── */
.ism-pmf-wrap {
  display: flex;
  flex-direction: column;
  background: var(--card-bg, #fff);
  border-radius: 18px;
  overflow: visible;
  font-family: "Inter", sans-serif;
}

/* ── Header ──────────────────────────────── */
.ism-pmf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
    padding: 22px 22px 0px 22px;
  /* background: var(--sidebar-bg, #1B2340); */
  /* border-bottom: 1px solid rgba(255,255,255,0.06); */
  flex-shrink: 0;
}

.ism-pmf-title {
  font-size: 15px;
  font-weight: 700;
  color: #f1f5f9;
  letter-spacing: -0.01em;
  margin: 0;
}

.ism-pmf-close {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  /* border: 1px solid var(--accent); */
  /* background: rgba(255, 255, 255, 0.06); */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  flex-shrink: 0;
}
.ism-pmf-close:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* ── Body ────────────────────────────────── */
.ism-pmf-body {
  padding: 22px;
  overflow-y: auto;
  max-height: 72vh;
  background: var(--card-bg, #fff);
  
  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
}
.ism-pmf-body::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* ── Footer ──────────────────────────────── */
.ism-pmf-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0px 22px 22px 22px;
  /* background: var(--sidebar-bg, #1B2340); */
  /* border-top: 1px solid rgba(255,255,255,0.06); */
  flex-shrink: 0;
}

/* Submit button */
.ism-pmf-btn-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #f26522, #c94f10);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(242, 101, 34, 0.35);
  white-space: nowrap;
}
.ism-pmf-btn-submit:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(242, 101, 34, 0.45);
}
.ism-pmf-btn-submit:active:not(:disabled) {
  transform: translateY(0);
}
.ism-pmf-btn-submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
